import React, { Dispatch, useState } from 'react';
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  InputLabel,
  Stack,
  TextField
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { ApplicationAction, ApplicationState, IApplication, IApplicationTask } from '../types';
import dayjs from 'dayjs';
import StyledModal from '../../Components/GenericModal/StyledModal';
import {
  FormTextField,
  MultilineFormTextField
} from '../../Components/CustomUIElements/FormTextField';
import { SingleDateTimePicker } from '../../Components/CustomUIElements/SingleDatePicker';
import { sharedClasses } from '../../Components/CustomUIElements/sharedClasses';
import { classes as importClasses } from '../../../ScoutCalendar/CreateEvent/EventProgress/styles';
import FormSelectWithIconField from '../../Components/CustomUIElements/FormSelectWithIconField';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Api from '../API';
import { IAssignableUsers } from '../../ApprovalForms/types';
import ModalFooterButtons from '../../Components/GenericModal/ModalFooterButtons';
import GenericDialog from '../../Components/Modals/GenericDialog';
import { styles } from '../../Candidate/styles';
import { useGetAssignableUsers } from '../helper';
import { ModalType } from '../config';

const priorities = [
  { label: 'Low', value: '1', color: '#8CCF95' },
  { label: 'Normal', value: '0', color: '#E6A052' },
  { label: 'High', value: '-1', color: '#E37D7A' }
];

export default function AddEditTask({
  ApplicationState,
  dispatch
}: {
  ApplicationState: ApplicationState;
  dispatch: Dispatch<ApplicationAction>;
}) {
  const { modalsOpen, editedTask } = ApplicationState;
  const [subject, setSubject] = useState<string>(editedTask?.subject || '');
  const [note, setNote] = useState<string>(editedTask?.note || '');
  const [date, setDate] = useState<Date>(new Date(editedTask?.due_at || Date.now()));
  const [priority, setPriority] = useState<{ label: string; value: string; color: string }>(
    priorities.find((p) => p.label === editedTask?.priority) || priorities[0]
  );
  const [assignedUser, setAssignedUser] = useState<IAssignableUsers | null>(null);
  const [taskCompleted, setTaskCompleted] = useState<boolean>(!!editedTask?.completed_at);
  const [deleteTaskDialogOpen, setDeleteTaskDialogOpen] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();
  const application = queryClient.getQueryData<IApplication>(['application']);
  const tasks = queryClient.getQueryData<IApplicationTask[]>(['tasks']);

  const { assignableUsers, loadingAssignableUsers } = useGetAssignableUsers({ dispatch });

  const { mutate: addTask, isLoading: addingTask } = useMutation({
    mutationFn: async () => {
      if (application && assignedUser) {
        const { res } = await Api.addTask(application.job.id, application.id, {
          subject,
          note,
          priority: Number(priority.value),
          due_at: dayjs(date).toISOString(),
          user_id: assignedUser.id
        });
        return res;
      }
    },
    onSuccess: (res) => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: `Task has been successfully added`, state: 'success' }
      });
      tasks && queryClient.setQueryData(['tasks'], [...tasks, res]);
      handleClose();
    },
    onError: (error: { error: string }) => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error adding a task, ${error.error}`,
          state: 'error'
        }
      });
    }
  });

  const { mutate: updateTask, isLoading: updatingTask } = useMutation({
    mutationFn: async () => {
      if (application && editedTask) {
        const { res } = await Api.updateApplicationTask(
          application.job.id,
          application.id,
          editedTask.id,
          {
            subject,
            note,
            priority: Number(priority.value),
            due_at: dayjs(date).toISOString(),
            completed_at: taskCompleted ? dayjs().toISOString() : null
          }
        );
        return res;
      }
    },
    onSuccess: (res) => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: `Task has been successfully updated`, state: 'success' }
      });
      if (tasks) {
        const newTasks = [...tasks];
        const index = tasks.findIndex((t) => t.id === editedTask?.id);
        newTasks[index] = res;
        queryClient.setQueryData(['tasks'], newTasks);
      }
      handleClose();
    },
    onError: (error: { error: string }) => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error in updating task, ${error.error}`,
          state: 'error'
        }
      });
    }
  });

  const { mutate: deleteTask, isLoading: deletingTask } = useMutation({
    mutationFn: async () => {
      if (application && editedTask) {
        Api.deleteTask(application?.job.id, application?.id, editedTask.id);
      }
    },
    onSuccess: () => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: `Task has been successfully deleted`, state: 'success' }
      });
      const newTasks = tasks?.filter((task) => task.id !== editedTask?.id);
      queryClient.setQueryData(['tasks'], newTasks);
      handleClose();
    },
    onError: (error: { error: string }) => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error in deleting task, ${error.error}`,
          state: 'error'
        }
      });
    }
  });

  const handleClose = () => {
    dispatch({ type: 'SET_EDITED_TASK', payload: null });
    dispatch({ type: 'SET_MODALS_OPEN', payload: null });
  };

  const DeleteDialogDescription = () => (
    <Stack>
      <Stack>Are you sure you want to delete the current task?</Stack>
      <Stack>{`You will lose the task you've made.`}</Stack>
    </Stack>
  );

  const validateInputs = () => {
    setErrors({});
    const inputErrors: Record<string, string> = {};
    if (!subject.trim()) inputErrors.subject = 'Subject can not be empty';
    if (!note.trim()) inputErrors.note = 'Note can not be empty';
    if (!assignedUser && !editedTask) inputErrors.user = 'Assigned user can not be empty';
    setErrors(inputErrors);
    return !Object.keys(inputErrors).length;
  };

  const handleSubmit = () => {
    if (validateInputs()) {
      editedTask ? updateTask() : addTask();
    }
  };

  return (
    <StyledModal
      isOpen={modalsOpen === ModalType.ADD_EDIT_TASK}
      label="Add/Edit application task"
      handleClose={handleClose}
      styleOverrides={styles.modalContainer}
      closeIconId={`close-${editedTask ? 'edit' : 'add'}-task-button`}
    >
      <Stack sx={styles.modalContent}>
        <Stack sx={styles.modalTitle}>{editedTask ? 'Edit' : 'Add'} task</Stack>
        <Stack sx={styles.modalCandidateName}>
          {`Application #${application?.id} - ${application?.candidate.firstname} ${application?.candidate.lastname}`}
        </Stack>
        <FormTextField
          label="Subject"
          value={subject}
          required
          onChange={(v) => setSubject(v.target.value)}
          error={errors.subject}
        />
        <MultilineFormTextField
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
          rows={5}
          required
          error={errors.note}
        />
        <Stack sx={{ flexDirection: 'row', columnGap: 5 }}>
          <Stack sx={{ width: '45%', flexDirection: 'row', columnGap: 2 }}>
            <Stack>
              <SingleDateTimePicker
                inputStyles={importClasses.datePicker}
                label={
                  <InputLabel htmlFor="text-input" sx={sharedClasses.inputLabel}>
                    Expiry date and time<span>*</span>
                  </InputLabel>
                }
                value={date}
                onChange={(value) => value && setDate(value)}
              />
            </Stack>
          </Stack>
          <FormSelectWithIconField
            options={priorities}
            defaultValue={priority.value}
            onChange={(value) =>
              setPriority(priorities.find((p) => p.value === value) || priorities[0])
            }
            label={'Priority'}
            styles={styles.prioritySelectStyling}
          />
        </Stack>
        {!editedTask && (
          <Autocomplete
            disablePortal
            id="add-application-task-assigned-users-select"
            options={assignableUsers || []}
            getOptionLabel={(option: IAssignableUsers) =>
              `${option?.firstname} ${option?.lastname}`
            }
            value={assignedUser}
            sx={{ ...sharedClasses.formAutocomplete, paddingTop: 3, width: '45%' }}
            loading={loadingAssignableUsers}
            loadingText="Loading assignable users..."
            ListboxProps={{ style: styles.listboxProps }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assign to user"
                InputLabelProps={{ shrink: true }}
                sx={styles.autoCompleteTextfield}
                required
                error={!assignedUser}
                helperText={!assignedUser && errors.user}
              />
            )}
            onChange={(event, value) => setAssignedUser(value)}
          />
        )}
        {editedTask && (
          <FormControlLabel
            control={
              <Checkbox
                id="mark-as-complete-check-box"
                sx={sharedClasses.formCheckbox}
                checked={taskCompleted}
                onClick={() => setTaskCompleted(!taskCompleted)}
                disableRipple
              />
            }
            label="Mark as complete"
            sx={sharedClasses.checkboxLabel}
          />
        )}
        <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          {editedTask && (
            <Button
              id="delete-task-button"
              sx={{
                ...sharedClasses.genericRedButton,
                marginTop: '10px',
                columnGap: 0.5,
                minWidth: '135px'
              }}
              onClick={() => setDeleteTaskDialogOpen(true)}
            >
              <DeleteIcon />
              Delete task
            </Button>
          )}
          <ModalFooterButtons
            primaryButtonText={editedTask ? 'Save' : 'Submit'}
            primaryButtonCallback={handleSubmit}
            secondaryButtonText="Cancel"
            secondaryButtonCallback={handleClose}
            isLoading={addingTask || updatingTask}
            primaryButtonID={`${editedTask ? 'save' : 'submit'}-task-button`}
            secondaryButtonID={`cancel-${editedTask ? 'edit' : 'add'}-task-button`}
            primaryButtonMinWidth={editedTask ? '75px' : '90px'}
          />
        </Stack>
      </Stack>
      <GenericDialog
        isDialogOpen={deleteTaskDialogOpen}
        setDialogOpen={setDeleteTaskDialogOpen}
        title={`Delete task?`}
        description={DeleteDialogDescription()}
        buttonCallback={() => deleteTask()}
        callbackLoading={deletingTask}
        buttonText="Delete"
        secondaryButtonText="Cancel"
        url=""
        primaryButtonId="confirm-delete-task-button"
        secondaryButtonId="cancel-delete-task-button"
      />
    </StyledModal>
  );
}
